import { useMemo } from "react";

import Constants from "../constants/Index";
import { normalizeToScale } from "../utils/numbers/normalizeToPercent";
import { getColorFromValue } from "../utils/colors/colorFromAScale";

export type PastureView = 
  | 'capacity-immediate'
  | 'capacity-average' 
  | 'paddock-time'
  | 'management-type'
  | 'management-height'
  | 'management-rate'
  | 'management-day'
  | 'default-map';

type UsePaddockMapColorsProps = {
  view: PastureView;
  microAreas: IMicroArea[];
  areas: IArea[];
  farmFilter: any;
  areaFilter: any;
  microAreaFilter: any;
  batchFilter: any;
  rateSelected?: number;
};

export function usePaddockMapColors({
  view,
  microAreas,
  areas,
  farmFilter,
  areaFilter,
  microAreaFilter,
  batchFilter,
  rateSelected = 0
}: UsePaddockMapColorsProps) {
  
  // Calcular valores máximos para gradientes
  const maxValues = useMemo(() => {
    if (!microAreas.length) return { maxCapacity: 0, maxDescanso: 0, maxOcupacao: 0, maxHeight: 0, areaAverages: new Map(), farmAverage: 0 };
    
    const filteredMicroAreas = microAreas.filter(ma => 
      !areaFilter || ma.area_id === Number(areaFilter.id)
    );

    let maxCapacity = 0;
    let maxDescanso = 0;
    let maxOcupacao = 0;
    let maxHeight = 0;
    const areaAverages = new Map<string, number>();
    let farmAverage = 0;
    let areaPaddockTimeData = new Map();
    let farmPaddockTimeData = null;

    if (view === 'capacity-immediate' || view === 'capacity-average') {
      maxCapacity = view === 'capacity-average' 
        ? 8 
        : Math.max(...filteredMicroAreas.map(ma => ma.instant_lot_rate || 0));
      
      // Calcular médias por área para capacity-immediate e capacity-average
      if (view === 'capacity-immediate' || view === 'capacity-average') {
        // Agrupar microareas por área
        const microAreasByArea = new Map<number, IMicroArea[]>();
        filteredMicroAreas.forEach(ma => {
          const areaId = ma.area_id;
          if (!microAreasByArea.has(areaId)) {
            microAreasByArea.set(areaId, []);
          }
          microAreasByArea.get(areaId)!.push(ma);
        });

        // Calcular média para cada área
        microAreasByArea.forEach((microAreasInArea, areaId) => {
          const validRates = microAreasInArea
            .map(ma => view === 'capacity-immediate' ? ma.instant_lot_rate : ma.average_lot_rate)
            .filter(rate => rate !== null && rate !== undefined);
          
          if (validRates.length > 0) {
            const average = validRates.reduce((sum, rate) => sum + rate, 0) / validRates.length;
            areaAverages.set(areaId.toString(), average);
            
          }
        });

        // Calcular média geral da fazenda
        const allAreaAverages = Array.from(areaAverages.values());
        if (allAreaAverages.length > 0) {
          farmAverage = allAreaAverages.reduce((sum, avg) => sum + avg, 0) / allAreaAverages.length;
          
        }
      }
    } else if (view === 'paddock-time') {
      maxDescanso = Math.max(...filteredMicroAreas.map(ma => ma.days_without_grazing));
      maxOcupacao = Math.max(...filteredMicroAreas.map(ma => ma.days_grazing));
      
      // Calcular dados agregados por área para paddock-time
      const microAreasByArea = filteredMicroAreas.reduce((acc, ma) => {
        const areaId = ma.area.id.toString();
        if (!acc[areaId]) acc[areaId] = [];
        acc[areaId].push(ma);
        return acc;
      }, {} as Record<string, typeof filteredMicroAreas>);
      
      Object.entries(microAreasByArea).forEach(([areaId, areaMicroAreas]) => {
        const descansoMicroAreas = areaMicroAreas.filter(ma => ma.days_grazing === 0);
        const ocupacaoMicroAreas = areaMicroAreas.filter(ma => ma.days_grazing !== 0);
        
        const dominantState = descansoMicroAreas.length >= ocupacaoMicroAreas.length ? 'descanso' : 'ocupacao';
        const dominantMicroAreas = dominantState === 'descanso' ? descansoMicroAreas : ocupacaoMicroAreas;
        
        if (dominantMicroAreas.length > 0) {
          const values = dominantMicroAreas.map(ma => 
            dominantState === 'descanso' ? ma.days_without_grazing : ma.days_grazing
          );
          const averageValue = values.reduce((sum, val) => sum + val, 0) / values.length;
          const maxReference = dominantState === 'descanso' ? maxDescanso : maxOcupacao;
          
          areaPaddockTimeData.set(areaId, {
            dominantState,
            averageValue,
            maxReference
          });
        }
      });
      
      // Calcular dados agregados por fazenda para paddock-time
      const descansoMicroAreas = filteredMicroAreas.filter(ma => ma.days_grazing === 0);
      const ocupacaoMicroAreas = filteredMicroAreas.filter(ma => ma.days_grazing !== 0);
      
      const farmDominantState = descansoMicroAreas.length >= ocupacaoMicroAreas.length ? 'descanso' : 'ocupacao';
      const farmDominantMicroAreas = farmDominantState === 'descanso' ? descansoMicroAreas : ocupacaoMicroAreas;
      
      if (farmDominantMicroAreas.length > 0) {
        const farmValues = farmDominantMicroAreas.map(ma => 
          farmDominantState === 'descanso' ? ma.days_without_grazing : ma.days_grazing
        );
        const farmAverageValue = farmValues.reduce((sum, val) => sum + val, 0) / farmValues.length;
        const farmMaxReference = farmDominantState === 'descanso' ? maxDescanso : maxOcupacao;
        
        farmPaddockTimeData = {
          dominantState: farmDominantState,
          averageValue: farmAverageValue,
          maxReference: farmMaxReference
        };
      }
    } else if (view === 'management-height') {
      const heights = filteredMicroAreas
        .filter(ma => ma.management?.type?.monitoring_types?.some(el => el.id === 2))
        .map(ma => {
          const heightMonitoring = ma.monitory_values
            ?.filter(mv => mv.monitoring_type_id.id === 2);
          const lastValue = heightMonitoring?.length 
            ? heightMonitoring[heightMonitoring.length - 1]
            : null;
          return lastValue?.value || 0;
        });
      maxHeight = heights.length ? Math.max(...heights) : 0;
    } else if (view === 'management-rate') {
      // Para management-rate, o maxHeight é baseado na seleção de nota de referência
      maxHeight = rateSelected === 0 ? 5 : 10;
    } else if (view === 'management-day') {
      const grazing = filteredMicroAreas
        .filter(ma => ma.management?.type?.monitoring_types?.some(el => el.id === 3))
        .map(ma => ma.days_grazing || 0);
      const withoutGrazing = filteredMicroAreas
        .filter(ma => ma.management?.type?.monitoring_types?.some(el => el.id === 3))
        .map(ma => ma.days_without_grazing || 0);
      maxOcupacao = grazing.length ? Math.max(...grazing) : 0;
      maxDescanso = withoutGrazing.length ? Math.max(...withoutGrazing) : 0;
    }

    return { 
      maxCapacity, 
      maxDescanso, 
      maxOcupacao, 
      maxHeight, 
      areaAverages, 
      farmAverage,
      areaPaddockTimeData,
      farmPaddockTimeData
    };
  }, [view, microAreas, areaFilter, rateSelected]);

  // Função para calcular cor da microarea
  const getMicroAreaColor = useMemo(() => {
    return (microArea: IMicroArea) => {
      switch (view) {
        case 'capacity-immediate':
        case 'capacity-average': {
          // Para capacity, não verificar management - usar apenas o valor de lotação
          const value = view === 'capacity-average' 
            ? microArea.average_lot_rate 
            : microArea.instant_lot_rate;
          const currentValueColor = normalizeToScale(0, maxValues.maxCapacity, value || 0);
          const color = getColorFromValue(
            currentValueColor, 
            [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100], 
            ["#FFFFFF", "#FFEAD8", "#FFD5B1", "#FFC08A", "#FFAB63", "#FF9501", "#FF7727", "#FF593E", "#FF3C58", "#FF1E79", "#FF0000"]
          );
          
          
          return color;
        }

        case 'paddock-time': {
          const { days_grazing, days_without_grazing } = microArea;
          const currentGrazingTime = days_grazing === 0 ? maxValues.maxDescanso : maxValues.maxOcupacao;
          const currentValueColor = normalizeToScale(
            0, 
            currentGrazingTime, 
            days_grazing !== 0 ? days_grazing : currentGrazingTime - days_without_grazing
          );
          
          if (days_grazing === 0) {
            return getColorFromValue(currentValueColor, [0, 50, 100], ["#7B61FF", "#61FFF6", "#57CE71"]);
          } else {
            return getColorFromValue(currentValueColor, [0, 50, 100], ["#FFCA80", "#FF9501", "#FF6161"]);
          }
        }

        case 'management-type': {
          // Para management-type, verificar management
          if (!microArea.management?.type?.monitoring_types?.length) {
            return Constants.PASTUREMANAGEMENT_COLORS.WHITE;
          }
          
          switch (microArea.management?.type?.id) {
            case 1: return Constants.PASTUREMANAGEMENT_COLORS.WHITE;
            case 2: return Constants.PASTUREMANAGEMENT_COLORS.ORANGE;
            case 3: return Constants.PASTUREMANAGEMENT_COLORS.RED;
            case 4: return Constants.PASTUREMANAGEMENT_COLORS.BLUE;
            default: return Constants.PASTUREMANAGEMENT_COLORS.WHITE;
          }
        }

        case 'management-height': {
          // Para management-height, verificar management
          if (!microArea.management?.type?.monitoring_types?.length) {
            return Constants.PASTUREMANAGEMENT_COLORS.WHITE;
          }
          
          if (microArea.management?.type?.monitoring_types?.find(el => el.id === 2)) {
            const monitoringValue = microArea.monitory_values?.find(el => el.monitoring_type_id.id === 2);
            if (monitoringValue) {
              const currentValueColor = normalizeToScale(0, maxValues.maxHeight, monitoringValue.value);
              return getColorFromValue(currentValueColor, [0, 100], ["#FF6161", "#57CE71"]);
            }
            return Constants.PASTUREMANAGEMENT_COLORS.YELLOW;
          }
          return Constants.PASTUREMANAGEMENT_COLORS.WHITE;
        }

        case 'management-rate': {
          // Para management-rate, verificar management
          if (!microArea.management?.type?.monitoring_types?.length) {
            return Constants.PASTUREMANAGEMENT_COLORS.WHITE;
          }
          
          if (microArea.management?.type?.monitoring_types?.find(el => el.id === 1)) {
            const monitoringValue = microArea.monitory_values?.find(el => el.monitoring_type_id.id === 1);
            if (monitoringValue) {
              const maxValue = rateSelected === 0 ? 5 : 10;
              const currentValueColor = normalizeToScale(0, maxValue, monitoringValue.value);
              return getColorFromValue(currentValueColor, [0, 100], ["#FF6161", "#57CE71"]);
            }
            return Constants.PASTUREMANAGEMENT_COLORS.YELLOW;
          }
          return Constants.PASTUREMANAGEMENT_COLORS.WHITE;
        }

        case 'management-day': {
          // Para management-day, verificar management
          if (!microArea.management?.type?.monitoring_types?.length) {
            return Constants.PASTUREMANAGEMENT_COLORS.WHITE;
          }
          
          if (microArea.management?.type?.monitoring_types?.find(el => el.id === 3)) {
            const { days_grazing, days_without_grazing } = microArea;
            const currentGrazingTime = days_grazing === 0 ? maxValues.maxDescanso : maxValues.maxOcupacao;
            const currentValueColor = normalizeToScale(
              0, 
              currentGrazingTime, 
              days_grazing !== 0 ? days_grazing : currentGrazingTime - days_without_grazing
            );
            
            if (days_grazing === 0) {
              return getColorFromValue(currentValueColor, [0, 50, 100], ["#7B61FF", "#61FFF6", "#57CE71"]);
            } else {
              return getColorFromValue(currentValueColor, [0, 50, 100], ["#FFCA80", "#FF9501", "#FF6161"]);
            }
          }
          return Constants.PASTUREMANAGEMENT_COLORS.WHITE;
        }

        case 'default-map':
          return Constants.LEAFLET_NORMAL_BLUE.fillColor;

        default:
          return Constants.LEAFLET_NORMAL_BLUE.fillColor;
      }
    };
  }, [view, maxValues, rateSelected]);

  // Função para calcular cor da area
  const getAreaColor = useMemo(() => {
    return (farmId: string, areaId: string) => {
      if (farmId === farmFilter?.id) {
        if (areaFilter && areaId !== areaFilter.id) return Constants.LEAFLET_UNFOCUS.color;
      }
      
      // Para capacity-immediate e capacity-average, calcular cor baseada na média de lotação da área
      if (view === 'capacity-immediate' || view === 'capacity-average') {
        const areaAverage = maxValues.areaAverages.get(areaId);
        if (areaAverage !== undefined && maxValues.maxCapacity > 0) {
          const currentValueColor = normalizeToScale(0, maxValues.maxCapacity, areaAverage);
          return getColorFromValue(
            currentValueColor, 
            [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100], 
            ["#FFFFFF", "#FFEAD8", "#FFD5B1", "#FFC08A", "#FFAB63", "#FF9501", "#FF7727", "#FF593E", "#FF3C58", "#FF1E79", "#FF0000"]
          );
        }
      }
      
      // Para paddock-time, calcular cor baseada na média das microareas com estado dominante
      if (view === 'paddock-time' && maxValues.areaPaddockTimeData) {
        const areaData = maxValues.areaPaddockTimeData.get(areaId);
        if (areaData) {
          const currentValueColor = normalizeToScale(0, areaData.maxReference, areaData.averageValue);
          
          if (areaData.dominantState === 'descanso') {
            return getColorFromValue(currentValueColor, [0, 50, 100], ["#7B61FF", "#61FFF6", "#57CE71"]);
          } else {
            return getColorFromValue(currentValueColor, [0, 50, 100], ["#FFCA80", "#FF9501", "#FF6161"]);
          }
        }
      }
      
      return Constants.LEAFLET_NORMAL_BLUE.fillColor;
    };
  }, [farmFilter, areaFilter, view, maxValues]);

  // Função para calcular opacidade
  const getOpacity = useMemo(() => {
    return (farmId: string, areaId: string, microAreaId?: string) => {
      if (microAreaId && microAreaFilter && microAreaId.toString() !== microAreaFilter.id.toString()) return 0.2;
      if (areaFilter && areaId.toString() !== areaFilter.id.toString()) return 0.2;
      return 0.55;
    };
  }, [farmFilter, areaFilter, microAreaFilter]);

  // Função para calcular cor da borda
  const getBorderColor = useMemo(() => {
    return (farmId: string, areaId: string) => {
      if (farmId !== farmFilter?.id) return Constants.PADDOCKTIME_COLORS.WHITE;
      if (areaFilter && areaId !== areaFilter.id) return Constants.LEAFLET_UNFOCUS.color;
      return Constants.PADDOCKTIME_COLORS.WHITE;
    };
  }, [farmFilter, areaFilter]);

  // Função para calcular cor do batch
  const getBatchColor = useMemo(() => {
    return (batch: any) => {
      const microArea = microAreas.find(ma => ma.id === batch.currentMicroArea);
      if (!microArea?.management?.type?.monitoring_types?.length) {
        return "#FFF";
      }

      switch (view) {
        case 'management-rate': {
          if (microArea.management?.type?.monitoring_types?.find(el => el.id === 1)) {
            const lastValue = microArea.monitory_values
              ?.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
              .find(el => el.monitoring_type_id.id === 1);
            
            if (lastValue) {
              if (lastValue.value > microArea.management.requirement_in) return Constants.PASTUREMANAGEMENT_COLORS.GREEN;
              if (lastValue.value <= microArea.management.requirement_in && lastValue.value >= microArea.management.requirement_out) return Constants.PASTUREMANAGEMENT_COLORS.CYAN;
              return Constants.PASTUREMANAGEMENT_COLORS.RED;
            }
            return Constants.PASTUREMANAGEMENT_COLORS.YELLOW;
          }
          return "#FFF";
        }

        case 'management-height': {
          if (microArea.management?.type?.monitoring_types?.find(el => el.id === 2)) {
            const lastValue = microArea.monitory_values
              ?.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
              .find(el => el.monitoring_type_id.id === 2);
            
            if (lastValue) {
              if (lastValue.value > microArea.management.requirement_in) return Constants.PASTUREMANAGEMENT_COLORS.GREEN;
              if (lastValue.value <= microArea.management.requirement_in && lastValue.value >= microArea.management.requirement_out) return Constants.PASTUREMANAGEMENT_COLORS.CYAN;
              return Constants.PASTUREMANAGEMENT_COLORS.RED;
            }
            return Constants.PASTUREMANAGEMENT_COLORS.YELLOW;
          }
          return "#FFF";
        }

        case 'management-day': {
          if (microArea.management?.type?.monitoring_types?.find(el => el.id === 3)) {
            if (microArea.days_grazing > 0) {
              if (microArea.days_grazing > microArea.management.requirement_in) return Constants.PASTUREMANAGEMENT_COLORS.RED;
              if (microArea.days_grazing <= microArea.management.requirement_in) return Constants.PASTUREMANAGEMENT_COLORS.GREEN;
            } else if (microArea.days_without_grazing > 0) {
              if (microArea.days_without_grazing > microArea.management.requirement_out) return Constants.PASTUREMANAGEMENT_COLORS.RED;
              if (microArea.days_without_grazing <= microArea.management.requirement_out) return Constants.PASTUREMANAGEMENT_COLORS.GREEN;
            }
          }
          return "#FFF";
        }

        case 'management-type':
        case 'default-map':
          return "#FFF";

        default:
          return "#FFF";
      }
    };
  }, [view, microAreas]);

  // Função para calcular cor da farm baseada na média geral
  const getFarmColor = useMemo(() => {
    return (farmId: string) => {
      // Para capacity-immediate e capacity-average, calcular cor baseada na média geral da fazenda
      if (view === 'capacity-immediate' || view === 'capacity-average') {
        if (maxValues.farmAverage > 0 && maxValues.maxCapacity > 0) {
          const currentValueColor = normalizeToScale(0, maxValues.maxCapacity, maxValues.farmAverage);
          const color = getColorFromValue(
            currentValueColor, 
            [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100], 
            ["#FFFFFF", "#FFEAD8", "#FFD5B1", "#FFC08A", "#FFAB63", "#FF9501", "#FF7727", "#FF593E", "#FF3C58", "#FF1E79", "#FF0000"]
          );
          
          
          return color;
        }
      }
      
      // Para paddock-time, calcular cor baseada na média das microareas com estado dominante da fazenda
      if (view === 'paddock-time' && maxValues.farmPaddockTimeData) {
        const farmData = maxValues.farmPaddockTimeData;
        const currentValueColor = normalizeToScale(0, farmData.maxReference, farmData.averageValue);
        
        if (farmData.dominantState === 'descanso') {
          return getColorFromValue(currentValueColor, [0, 50, 100], ["#7B61FF", "#61FFF6", "#57CE71"]);
        } else {
          return getColorFromValue(currentValueColor, [0, 50, 100], ["#FFCA80", "#FF9501", "#FF6161"]);
        }
      }
      
      return Constants.LEAFLET_NORMAL_BLUE.fillColor;
    };
  }, [view, maxValues]);

  return {
    getMicroAreaColor,
    getAreaColor,
    getFarmColor,
    getOpacity,
    getBorderColor,
    getBatchColor,
    maxValues
  };
}
